import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ChevronLeft, Loader2, Utensils } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CUISINE_MOODS = [
  { value: "Everything", label: "Surprise me", emoji: "🎲" },
  { value: "American", label: "Comfort food", emoji: "🍔" },
  { value: "Italian", label: "Pasta mood", emoji: "🍝" },
  { value: "Mexican", label: "Taco time", emoji: "🌮" },
  { value: "Asian", label: "Asian flavors", emoji: "🍜" },
  { value: "Indian", label: "Spice it up", emoji: "🍛" },
  { value: "Mediterranean", label: "Fresh & healthy", emoji: "🥗" },
];

const PRICE_OPTIONS = [
  { value: 1, label: "$" },
  { value: 2, label: "$$" },
  { value: 3, label: "$$$" },
];

export default function SetupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(true);
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: "",
  });
  const [radius, setRadius] = useState(3); // km
  const [priceRange, setPriceRange] = useState([1, 2, 3]);
  const [cuisine, setCuisine] = useState("Everything");
  const [hostName, setHostName] = useState("");

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation((prev) => ({ ...prev, latitude, longitude }));
          
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            if (data.display_name) {
              const parts = data.display_name.split(",");
              const shortAddress = parts.slice(0, 2).join(",").trim();
              setLocation((prev) => ({ ...prev, address: shortAddress }));
            }
          } catch (e) {
            setLocation((prev) => ({ ...prev, address: "Current location" }));
          }
          setDetectingLocation(false);
        },
        (error) => {
          setLocation({
            latitude: 40.7128,
            longitude: -74.006,
            address: "New York, NY",
          });
          setDetectingLocation(false);
        }
      );
    } else {
      setLocation({
        latitude: 40.7128,
        longitude: -74.006,
        address: "New York, NY",
      });
      setDetectingLocation(false);
    }
  }, []);

  const togglePrice = (value) => {
    if (priceRange.includes(value)) {
      if (priceRange.length > 1) {
        setPriceRange(priceRange.filter((p) => p !== value));
      }
    } else {
      setPriceRange([...priceRange, value].sort());
    }
  };

  const handleSubmit = async () => {
    if (!hostName.trim()) {
      toast.error("What's your name?");
      return;
    }
    if (!location.latitude || !location.longitude) {
      toast.error("Still detecting location...");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/sessions`, {
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        price_range: priceRange,
        cuisine: cuisine,
        host_name: hostName.trim(),
        radius_km: radius,
      });

      const { session, participant } = response.data;
      
      sessionStorage.setItem("participantId", participant.id);
      sessionStorage.setItem("participantName", participant.name);
      sessionStorage.setItem("isHost", "true");
      
      navigate(`/invite/${session.id}`);
    } catch (error) {
      console.error("Failed to create session:", error);
      toast.error("Couldn't create session. Try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="app-container">
      <div className="flex-1 flex flex-col px-6 py-4 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md active:scale-95 transition-transform"
          >
            <ChevronLeft className="w-6 h-6 text-forkyeah-main" strokeWidth={2.5} />
          </button>
          <div>
            <h1 className="text-xl font-black text-forkyeah-main">Quick setup</h1>
            <p className="text-xs text-forkyeah-muted font-semibold">30 seconds to start</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-5"
        >
          {/* Your Name */}
          <div>
            <label className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider block mb-2">
              Your name
            </label>
            <input
              data-testid="host-name-input"
              type="text"
              placeholder="First name"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              className="input-field"
              autoFocus
            />
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider block mb-2">
              Where are you?
            </label>
            <div className="relative">
              <input
                data-testid="location-input"
                type="text"
                placeholder={detectingLocation ? "Finding you..." : "Location"}
                value={location.address}
                onChange={(e) => setLocation((prev) => ({ ...prev, address: e.target.value }))}
                className="input-field pl-12"
                disabled={detectingLocation}
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                {detectingLocation ? (
                  <Loader2 className="w-5 h-5 text-forkyeah-primary animate-spin" />
                ) : (
                  <MapPin className="w-5 h-5 text-forkyeah-primary" strokeWidth={2.5} />
                )}
              </div>
            </div>
          </div>

          {/* Distance Radius */}
          <div>
            <label className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider block mb-2">
              How far? <span className="text-forkyeah-primary">{radius} km</span>
            </label>
            <div className="px-2">
              <Slider
                data-testid="radius-slider"
                value={[radius]}
                onValueChange={([val]) => setRadius(val)}
                min={1}
                max={10}
                step={1}
                className="py-4"
              />
              <div className="flex justify-between text-xs text-forkyeah-muted font-semibold">
                <span>Walking</span>
                <span>Driving</span>
              </div>
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider block mb-2">
              Budget
            </label>
            <div className="flex gap-2">
              {PRICE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  data-testid={`price-${option.value}`}
                  onClick={() => togglePrice(option.value)}
                  className={`flex-1 h-12 rounded-xl font-black text-lg border-2 transition-all active:scale-95 ${
                    priceRange.includes(option.value)
                      ? "bg-forkyeah-primary text-white border-forkyeah-primary"
                      : "bg-white text-forkyeah-muted border-[#FFE8DD] hover:border-forkyeah-primary/50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cuisine Mood */}
          <div>
            <label className="text-xs font-bold text-forkyeah-muted uppercase tracking-wider block mb-2">
              What mood?
            </label>
            <Select value={cuisine} onValueChange={setCuisine}>
              <SelectTrigger 
                data-testid="cuisine-select"
                className="w-full h-12 bg-white border-2 border-[#FFE8DD] rounded-xl px-4 font-bold focus:border-forkyeah-primary"
              >
                <div className="flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-forkyeah-primary" />
                  <SelectValue placeholder="Pick a vibe" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {CUISINE_MOODS.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`cuisine-${c.value}`}>
                    <span className="flex items-center gap-2">
                      <span>{c.emoji}</span>
                      <span>{c.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Submit Button */}
        <div className="mt-auto pt-6">
          <motion.button
            data-testid="create-session-btn"
            onClick={handleSubmit}
            disabled={loading || detectingLocation || !hostName.trim()}
            className="btn-primary gap-2"
            whileTap={{ scale: 0.95 }}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating...
              </>
            ) : (
              "Continue"
            )}
          </motion.button>
        </div>
      </div>
    </main>
  );
}
